import { getLanguage } from 'cdui-js';
import { fetchRegionChildren } from '../../../api/base';
export type RegionOption = { label: string; value: string };

/** BaseRegionRespDTO（/base/region/children 返回 List） */
type RegionNode = {
  countryCode?: string;
  parentCode?: string;
  regionName?: string;
  regionNameEn?: string;
  regionCode?: string;
  regionLevel?: number;
  sortOrder?: number;
  status?: number;
};

const unwrapList = (data: unknown): RegionNode[] => {
  if (Array.isArray(data)) return data as RegionNode[];
  const row = data as Record<string, unknown>;
  if (Array.isArray(row?.list)) return row.list as RegionNode[];
  if (Array.isArray(row?.data)) return row.data as RegionNode[];
  const nested = row?.data as Record<string, unknown> | undefined;
  if (Array.isArray(nested?.list)) return nested.list as RegionNode[];
  return [];
};

const regionLabel = (item: RegionNode) => {
  const lang = String(getLanguage() || 'zh-CN');
  if (lang.startsWith('en')) return String(item.regionNameEn || item.regionName || '').trim();
  return String(item.regionName || item.regionNameEn || '').trim();
};

const activeSorted = (list: RegionNode[]) =>
  list
    .filter((item) => item && Number(item.status) !== 0)
    .sort((a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0));

const mapRows = (list: RegionNode[]): RegionOption[] =>
  activeSorted(list)
    .map((item) => {
      const value = String(item.regionCode ?? '').trim();
      const label = regionLabel(item);
      return value && label ? { label, value } : null;
    })
    .filter(Boolean) as RegionOption[];

async function fetchChildrenOptions(parentCode: string, regionLevel: number): Promise<RegionOption[]> {
  if (!parentCode) return [];
  const res = await fetchRegionChildren(parentCode, regionLevel);
  return mapRows(unwrapList(res));
}

/** 国家变更后拉省/州：parentCode = 国家 ISO（/sys/dict/country.code，如 CN） */
export async function loadProvinceOptions(countryIso: string): Promise<RegionOption[]> {
  try {
    return await fetchChildrenOptions(countryIso, 2);
  } catch {
    return [];
  }
}

/** 省变更后拉市（level 3） */
export async function loadCityOptions(provinceRegionCode: string): Promise<RegionOption[]> {
  try {
    return await fetchChildrenOptions(provinceRegionCode, 3);
  } catch {
    return [];
  }
}

/** 回填：记录里的 code 不在选项中时，用记录名称补一条，保证有展示 */
export const withRecordOption = (options: RegionOption[], code: string, name: string): RegionOption[] => {
  const value = String(code || '').trim();
  const label = String(name || '').trim();
  if (!value || !label || options.some((item) => item.value === value)) return options;
  return [...options, { label, value }];
};

export const regionLabelOf = (options: RegionOption[], code: string) =>
  options.find((item) => item.value === code)?.label || '';
